import type { ReactNode } from 'react';
import {
  Disclosure as HeadlessDisclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { ChevronDown } from 'lucide-react';

interface DisclosureProps {
  title: ReactNode;
  /** Open on first render. */
  defaultOpen?: boolean;
  testId?: string;
  children: ReactNode;
}

/**
 * Card-shaped disclosure: a full-width toggle header and a body the caller renders (no padding,
 * so tables can span edge to edge). Closed unless `defaultOpen`. Replaces the old `Accordion`.
 */
export function Disclosure({ title, defaultOpen = false, testId, children }: DisclosureProps) {
  return (
    <HeadlessDisclosure
      as="div"
      defaultOpen={defaultOpen}
      data-testid={testId}
      className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
    >
      {({ open }) => (
        <>
          <DisclosureButton className="group flex w-full items-center justify-between gap-4 px-6 py-4 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 dark:hover:bg-white/[0.03]">
            <span className="text-base font-medium text-gray-800 dark:text-white/90">{title}</span>
            <ChevronDown
              aria-hidden="true"
              className={`size-5 shrink-0 text-gray-400 transition-transform dark:text-gray-500 ${open ? 'rotate-180' : ''}`}
            />
          </DisclosureButton>
          <DisclosurePanel className="border-t border-gray-200 dark:border-gray-800">
            {children}
          </DisclosurePanel>
        </>
      )}
    </HeadlessDisclosure>
  );
}
